import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

const DISMISS_KEY = 'aivue_install_dismissed_at';
const DISMISS_DAYS = 14;

/**
 * Tiny "Install App" banner shown when Chrome/Android fires `beforeinstallprompt`.
 * Dismissed for 14 days by default. iOS Safari doesn't fire the event — those
 * users see a separate one-line tip below the share button instead.
 */
export const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Hide if recently dismissed
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (ts && Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000) return;

    // Hide if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone) return; // iOS

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try { await deferredPrompt.userChoice; } catch { /* noop */ }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[60] max-w-md w-[calc(100%-2rem)] bg-zinc-900/95 border border-orange-500/30 rounded-2xl shadow-2xl backdrop-blur-md px-4 py-3 flex items-center gap-3"
      style={{ bottom: 'calc(6rem + env(safe-area-inset-bottom, 0px))' }}
      data-testid="install-prompt"
    >
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-purple-500 flex items-center justify-center flex-shrink-0">
        <Download className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white">Install AUDIVUE</p>
        <p className="text-xs text-zinc-400">Add to your home screen for one-tap access.</p>
      </div>
      <button
        onClick={install}
        className="px-3 py-1.5 rounded-full bg-white text-black text-xs font-semibold hover:bg-zinc-200 transition-colors flex-shrink-0"
        data-testid="install-prompt-yes"
      >
        Install
      </button>
      <button
        onClick={dismiss}
        className="text-zinc-500 hover:text-white p-1 flex-shrink-0"
        aria-label="Dismiss install prompt"
        data-testid="install-prompt-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
